<template>
<section class="vpn-section install">
  <div class="vpn-section__inner">
    <h2 class="vpn-section__title">{{ installTitle }}</h2>
    <p class="vpn-section__lead">{{ installLead }}</p>
    <div class="install__grid">
      <article
        v-for="(item, index) in stepList"
        :key="item.title"
        class="install__card"
        :class="`install__card--${(index % 3) + 1}`"
      >
        <div class="install__head">
          <span class="install__badge">{{ index + 1 }}</span>
          <div class="install__card-title">{{ item.title }}</div>
        </div>
        <p>{{ item.desc }}</p>
      </article>
    </div>
    <div class="install__cta">
      <a class="vpn-btn vpn-btn--primary" href="/download/" @click.prevent="goDownload">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ installCta }}
      </a>
    </div>
  </div>
</section>
</template>

<script>
import homeLocaleContent from '@/mixins/homeLocaleContent';

export default {
  name: 'InstallStepsSection',
  mixins: [homeLocaleContent],
  computed: {
    installTitle() {
      return this.homeLocale?.installTitle || 'How to Download & Install Secure VPN App';
    },
    installLead() {
      return this.homeLocale?.installLead || 'Follow these simple steps to download and install Secure VPN:';
    },
    installCta() {
      return this.homeLocale?.installCta || 'Download Secure VPN';
    },
    stepList() {
      return this.homeLocale?.installSteps || this.defaultSteps;
    },
  },
  data() {
    return {
      defaultSteps: [
        {
          title: 'Visit the Download Page',
          desc: 'Go to the Secure VPN download page or Google Play Store to get the latest version of the app.',
        },
        {
          title: 'Download Secure VPN',
          desc: 'Tap the Download button and wait for the APK or app file to finish downloading on your device.',
        },
        {
          title: 'Install the App',
          desc: 'Open the downloaded file and tap Install. Wait until the installation process is complete.',
        },
        {
          title: 'Open and Connect',
          desc: 'Launch Secure VPN, select a server location, and tap Connect to start a secure VPN connection.',
        },
        {
          title: 'Enjoy Secure Browsing',
          desc: 'Your internet traffic is now protected. You can browse, stream, game, and use public Wi-Fi with a more secure connection.',
        },
      ],
    }
  },
  methods: {
    goDownload() {
      this.$router.push('/download/').catch(() => {
        window.location.assign('/download/')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/css/vpn-sections.scss';

.install {
  background:
    radial-gradient(ellipse 70% 50% at 10% 20%, rgba(138, 63, 252, 0.1), transparent 55%),
    radial-gradient(ellipse 60% 45% at 90% 80%, rgba(62, 84, 232, 0.08), transparent 50%),
    linear-gradient(180deg, #f4f0ff 0%, #ebe4ff 100%);
}

.install__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 24px 18px;
  align-items: stretch;
}

.install__card {
  position: relative;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border-radius: 18px;
  padding: 18px 18px 16px;
  overflow: hidden;
  border: 1px solid rgba(214, 204, 245, 0.8);
  box-shadow: 0 10px 24px rgba(90, 60, 180, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(120% 90% at 100% 0%, rgba(255, 255, 255, 0.55) 0%, transparent 45%),
      radial-gradient(100% 80% at 0% 100%, rgba(109, 74, 255, 0.12) 0%, transparent 50%);
  }

  .install__head,
  p {
    position: relative;
    z-index: 1;
  }

  p {
    margin: 0;
    color: #5b6475;
    font-size: 14px;
    line-height: 1.55;
    flex: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(90, 60, 180, 0.12);
  }
}

.install__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

}

.install__card-title {
  margin: 0;
  font-size: 16px;
  color: #2d2766;
  font-weight: 700;
  line-height: 1.3;
}

.install__card--1 {
  background: linear-gradient(160deg, #ffffff 0%, #f3eeff 55%, #ebe3ff 100%);
}

.install__card--2 {
  background: linear-gradient(160deg, #ffffff 0%, #eef4ff 55%, #e4ecff 100%);
}

.install__card--3 {
  background: linear-gradient(160deg, #ffffff 0%, #f5ecff 55%, #f0e6ff 100%);
}

.install__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #8a3ffc 0%, #5b3fe8 100%);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 8px 16px rgba(109, 74, 255, 0.28);
}

.install__cta {
  margin-top: 40px;
  text-align: center;

  .vpn-btn {
    min-width: min(300px, 100%);
    max-width: 100%;
    min-height: 42px;
    padding: 10px 56px;
    border-radius: 999px;
    font-size: 15px;
  }
}

@media (max-width: 900px) {
  .install__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .install__grid {
    grid-template-columns: 1fr;
  }

  .install__card {
    padding: 16px 14px 14px;
  }

  .install__cta {
    margin-top: 28px;

    .vpn-btn {
      width: 100%;
      min-width: 0;
      padding: 10px 20px;
    }
  }
}
</style>
